import React, { useState, useEffect, useMemo } from "react";
import PropTypes from 'prop-types';
import withRouter from "components/Common/withRouter";

import { 
  Row, 
  Col, 
  Badge, 
  Button, 
  Card, 
  CardBody, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Spinner 
} from "reactstrap";
import TableContainer from "../../components/Common/TableContainer";

// Typo düzeltildi: LatestTransaction
const LatestTransaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  
  // Rol Kontrolü
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const isAdmin = authUser?.Users_Type_ID === 3; // 3: Admin

  const toggleViewModal = () => setModal(!modal);

  const handleViewDetails = (transaction) => {
    setSelectedTx(transaction);
    toggleViewModal();
  };

  useEffect(() => {
    const fetchLatestTransactions = async () => {
      setIsLoading(true);
      try {
        const userId = authUser?.ID;
        if (!userId) return;

        // Admin ise tüm tabloyu çek, Sürücü ise sadece kendi ID'sini filtrele
        const filterStr = isAdmin ? "" : `$filter=Users_ID eq ${userId}&`;
        
        const response = await fetch(
          `https://hiflowapi.stechomeyazilim.info/PAYMENT_LOGS?${filterStr}$orderby=CreatedAt desc&$top=15&$expand=Users`
        );
        const result = await response.json();

        if (result && result.value) {
          const formattedData = result.value.map(item => ({
            id: item.ID,
            orderId: item.OrderNumber || "-",
            userName: item.Users ? item.Users.Users_NameSurname : "Bilinmeyen",
            userEmail: item.Users ? item.Users.Users_Email : "-",
            date: item.CreatedAt ? new Date(item.CreatedAt).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : "-",
            amount: item.TransactionAmount ? `${item.TransactionAmount} TL` : "0 TL", 
            status: item.Status || "Belirsiz",
            ipAddress: item.IpAddress || "-",
            errorMessage: item.ErrorMessage || "-" // Null gelirse - yap
          }));
          setTransactions(formattedData);
        }
      } catch (error) {
        console.error("Ödemeler çekilirken hata:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestTransactions();
  }, [isAdmin, authUser?.ID]);

  // Tablo Kolonları
  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "Sipariş No",
        accessorKey: "orderId",
        cell: (cellProps) => <span className="text-body fw-bold">{cellProps.row.original.orderId}</span>,
      },
      {
        header: "Tarih",
        accessorKey: "date",
      },
      {
        header: "Tutar",
        accessorKey: "amount",
      },
      {
        header: "Durum",
        accessorKey: "status",
        cell: (cellProps) => {
          const status = cellProps.row.original.status?.toLowerCase() || "";
          let color = "secondary";
          
          // Geliştirilmiş Durum Renklendirmesi
          if (status.includes("success") || status.includes("başarılı")) color = "success";
          else if (status.includes("fail") || status.includes("hata") || status.includes("red")) color = "danger";
          else if (status.includes("pending") || status.includes("bekliyor")) color = "warning";
          
          return (
            <Badge className={`font-size-11 badge-soft-${color}`}>
              {cellProps.row.original.status}
            </Badge>
          );
        },
      },
      {
        header: "Eylem",
        cell: (cellProps) => (
          <Button
            type="button"
            color="primary"
            className="btn-sm btn-rounded"
            outline
            onClick={() => handleViewDetails(cellProps.row.original)}
          >
            Detay
          </Button>
        ),
      },
    ];

    if (isAdmin) {
      baseColumns.splice(1, 0, {
        header: "Müşteri / Sürücü",
        accessorKey: "userName",
        cell: (cellProps) => (
          <div>
            <div className="fw-medium">{cellProps.row.original.userName}</div>
            <small className="text-muted">{cellProps.row.original.userEmail}</small>
          </div>
        )
      });
    }

    return baseColumns;
  }, [isAdmin]);

  return (
    <React.Fragment>
      <Modal isOpen={modal} toggle={toggleViewModal} centered>
        <ModalHeader toggle={toggleViewModal} className="border-bottom-0">İşlem Detayları</ModalHeader>
        <ModalBody>
          {selectedTx && (
            <div className="p-2">
              <Row className="mb-2">
                <Col sm={5} className="text-muted">Sipariş Numarası:</Col>
                <Col sm={7} className="fw-bold">{selectedTx.orderId}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={5} className="text-muted">Ödemeyi Yapan:</Col>
                <Col sm={7}>{selectedTx.userName}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={5} className="text-muted">İşlem Tarihi:</Col>
                <Col sm={7}>{selectedTx.date}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={5} className="text-muted">Tutar:</Col>
                <Col sm={7} className="text-primary fw-bold">{selectedTx.amount}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={5} className="text-muted">IP Adresi:</Col>
                <Col sm={7}><code className="text-dark">{selectedTx.ipAddress}</code></Col>
              </Row>
              <hr />
              <div className="mt-3">
                <h6 className="fw-bold">Hata / Durum Mesajı:</h6>
                {/* Güvenli Hata Mesajı Kontrolü */}
                {selectedTx.errorMessage && selectedTx.errorMessage !== "-" ? (
                  <div className="alert alert-danger p-2 mb-0 mt-2 font-size-13">
                    <i className="mdi mdi-alert-circle me-2"></i>{selectedTx.errorMessage}
                  </div>
                ) : (
                  <Badge color="success" className="p-2 px-3">İşlem Başarılı</Badge>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleViewModal}>Kapat</Button>
        </ModalFooter>
      </Modal>

      <Card>
        <CardBody>
          <div className="d-flex align-items-center mb-4">
            <div className="flex-grow-1">
              <h4 className="card-title mb-0">
                {isAdmin ? "Sistemdeki Son Ödemeler" : "Son Ödemeleriniz"}
              </h4>
            </div>
          </div>

          {isLoading ? (
             <div className="text-center py-5">
                <Spinner color="primary" /> <p className="mt-2 text-muted">Veriler alınıyor...</p>
             </div>
          ) : transactions.length > 0 ? (
            <TableContainer
              columns={columns}
              data={transactions}
              isGlobalFilter={false}
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light"
            />
          ) : (
             <div className="text-center text-muted py-5">
                 <i className="bx bx-receipt display-4 mb-3 opacity-50"></i>
                 <h5>Kayıt Bulunamadı</h5>
                 <p>Henüz herhangi bir ödeme işlemi gerçekleştirilmemiş.</p>
             </div>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default withRouter(LatestTransaction); // Export ismi de düzeltildi